import React, { useState } from "react";
import { AiOutlineWhatsApp } from "react-icons/ai";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaTelegramPlane,
} from "react-icons/fa";
import { FiGlobe, FiAlertTriangle, FiCalendar, FiShare2 } from "react-icons/fi";
import Fade from 'react-reveal/Fade';
import { WHATSAPP_RDV_URL } from "../../utils/whatsapp";
const ADMIN_EMAIL = "allintechsn@gmail.com"; 
const FORMSPREE_ENDPOINT = (typeof process !== "undefined" && process.env?.REACT_APP_FORMSPREE_CONTACT_ID)
  ? `https://formspree.io/f/${process.env.REACT_APP_FORMSPREE_CONTACT_ID}`
  : null;

interface SocialLink {
  label: string;
  href: string;
}

interface ContactFormValues {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
}

interface ContactSectionProps {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  whatsappNumber?: string;
  websiteUrl?: string;
  socialLinks?: SocialLink[];
  subjectOptions?: string[];
  onSubmit?: (values: ContactFormValues) => void;
}

const defaultSocialLinks: SocialLink[] = [
  { label: "Facebook", href: "https://www.facebook.com/FoutaBio/" },
  { label: "Instagram", href: "https://www.instagram.com/invites/contact/?i=1sbjiij4t3srb&utm_content=qwqnqq" },
  { label: "YouTube", href: "https://youtube.com/channel/UC3jiei2AdEb6k2RjQDa2VXA" },
  { label: "TikTok", href: "https://www.tiktok.com/@maitre_ventouse?_r=1&_t=ZS-99G3yLbWS5w" },
  { label: "Telegram", href: "https://t.me/foutabio" },
];

const defaultSubjectOptions: string[] = [
  "Accompagnement Hijama",
  "Phytothérapie",
  "Médecine prophétique",
  "Question sur un produit",
  "Autre demande",
];

const ContactSection: React.FC<ContactSectionProps> = ({
  eyebrow = "Nous joindre",
  title = "Contactez Fouta Bio",
  subtitle = "Vous souhaitez obtenir une information sur un accompagnement ou poser une question sur un produit ? Nous sommes disponibles par WhatsApp.",
  whatsappNumber = "+221771135964",
  websiteUrl = "www.foutabio.com",
  socialLinks = defaultSocialLinks,
  subjectOptions = defaultSubjectOptions,
  onSubmit,
}) => {
  // Formulaire masqué : le contact passe par WhatsApp. Code conservé.
  const SHOW_CONTACT_FORM = false;

  const [values, setValues] = useState<ContactFormValues>({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setSubmitStatus("idle");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (onSubmit) {
      onSubmit(values);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    if (FORMSPREE_ENDPOINT) {
      try {
        const res = await fetch(FORMSPREE_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            _replyto: values.email,
            prenom: values.firstName,
            nom: values.lastName,
            email: values.email,
            sujet: values.subject || "Non précisé",
            message: values.message,
          }),
        });
        if (res.ok) {
          setSubmitStatus("success");
          setValues({ firstName: "", lastName: "", email: "", subject: "", message: "" });
        } else {
          setSubmitStatus("error");
        }
      } catch {
        setSubmitStatus("error");
      }
      setIsSubmitting(false);
      return;
    }

    const subjectLine = encodeURIComponent(
      `Contact Fouta Bio - ${values.subject || "Nouvelle demande"} (${values.firstName} ${values.lastName})`
    );
    const body = encodeURIComponent(
      `Prénom: ${values.firstName}\nNom: ${values.lastName}\nEmail: ${values.email}\nSujet: ${values.subject || "Non précisé"}\n\nMessage:\n${values.message}`
    );
    window.location.href = `mailto:${ADMIN_EMAIL}?subject=${subjectLine}&body=${body}`;
    setIsSubmitting(false);
  };

  return (
    <div>
    <section id="contact" className="contact-section">
      {/* Bandeau d'en-tête */}
      <div className="contact-hero py-5">
        <div className="container-xl text-center">
          <Fade bottom>
          <p className="contact-eyebrow mb-3">{eyebrow}</p>
          <h1 className="contact-title mb-3">{title}</h1>
          <p className="contact-subtitle mx-auto mb-0">{subtitle}</p>
          </Fade>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="contact-body py-5">
        <div className="container-xl">
          <div className="row g-4">
            {/* Colonne gauche : canal principal + infos */}
            <div className={`col-12 ${SHOW_CONTACT_FORM ? "col-lg-4" : "col-lg-6"}`}>
              <div className="contact-whatsapp-card p-4 mb-4">
                <p className="contact-block-label mb-2">
                  <AiOutlineWhatsApp className="contact-block-icon" />
                  Canal principal
                </p>
                <h3 className="contact-whatsapp-title mb-2">Écrire sur WhatsApp</h3>
                <p className="contact-whatsapp-text mb-3">
                  Réponse rapide pour toute question sur un accompagnement, un
                  produit ou la prise de rendez-vous.
                </p>
                
                <a  href={`https://wa.me/message/PGFSB43ZO3S5K1`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-whatsapp-btn"
                >
                  <AiOutlineWhatsApp size={18} />
                  Ouvrir WhatsApp
                </a>
              </div>

              <div className="contact-info-block mb-4">
                <p className="contact-block-label mb-2">
                  <FiShare2 className="contact-block-icon" />
                  Réseaux sociaux
                </p>
                <div className="contact-social-pills">
                  {socialLinks.map((social) => (
                    
                  <a    key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-social-pill"
                    >
                      {social.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="contact-info-block mb-4">
                <p className="contact-block-label mb-2">
                  <FiGlobe className="contact-block-icon" />
                  Site web
                </p>
                
                 <a href={`https://${websiteUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-website-link"
                >
                  {websiteUrl}
                </a>
              </div>

              <div className="contact-info-block mb-4">
                <p className="contact-block-label mb-2">
                  <FiCalendar className="contact-block-icon" />
                  Rendez-vous
                </p>
                <p className="contact-rdv-text mb-3">
                  Pour réserver, écrivez-nous directement sur WhatsApp.
                </p>
                <a
                  href={WHATSAPP_RDV_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-rdv-btn"
                >
                  Prendre rendez-vous
                </a>
              </div>

              <div className="contact-warning d-flex align-items-start p-3">
                <FiAlertTriangle className="contact-warning-icon me-2" />
                <p className="mb-0">
                  Les échanges en ligne ne permettent pas d'établir un
                  diagnostic. WhatsApp est un canal d'information et
                  d'orientation.
                </p>
              </div>
            </div>

            {/* Colonne droite : formulaire — masqué, contact via WhatsApp */}
            {SHOW_CONTACT_FORM && (
            <div className="col-12 col-lg-8">
              <form className="contact-form" onSubmit={handleSubmit} noValidate={true}>
                <p className="contact-form-title mb-4">Envoyer un message</p>

                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="contact-label mb-1" htmlFor="firstName">
                      Prénom *
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      className="contact-input"
                      placeholder="Fatou"
                      value={values.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="contact-label mb-1" htmlFor="lastName">
                      Nom *
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      className="contact-input"
                      placeholder="Diallo"
                      value={values.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="contact-label mb-1" htmlFor="email">
                    Email *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="contact-input"
                    placeholder="votre@email.com"
                    value={values.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="contact-label mb-1" htmlFor="subject">
                    Sujet (optionnel)
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    className="contact-input contact-select"
                    value={values.subject}
                    onChange={handleChange}
                  >
                    <option value="">— Choisir un sujet —</option>
                    {subjectOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="contact-label mb-1" htmlFor="message">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className="contact-input contact-textarea"
                    placeholder="Décrivez votre demande..."
                    rows={5}
                    value={values.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <button type="submit" className="contact-submit-btn w-100" disabled={isSubmitting}>
                  {isSubmitting ? "Envoi..." : "Envoyer le message"}
                </button>

                {submitStatus === "success" && (
                  <p className="contact-form-success mt-3" role="status">
                    Message envoyé. Nous vous recontacterons rapidement.
                  </p>
                )}
                {submitStatus === "error" && (
                  <p className="contact-form-error mt-3" role="alert">
                    L'envoi a échoué. Réessayez ou contactez-nous directement à {ADMIN_EMAIL}.
                  </p>
                )}
              </form>
            </div>
            )}
          </div>
        </div>
      </div>
    </section>
    </div>
  );
};

export default ContactSection;